// Which ad brought a visitor here, kept in their browser with their consent.
//
// Two things, and they are not the same thing. A campaign is the UTM
// parameters, which describe the ad: they are ours, and they are what the
// reports are grouped by. A click identifier is issued by Meta, Reddit or
// Google and ties one purchase to one ad, only the platform can supply it,
// and only the platform can read it.
//
// Nothing here touches storage or the network; it is the arithmetic, and
// `sales-tracking.js` is what wires it to a browser.

// How long a campaign is remembered. Independent of how long the consent that
// allowed it is remembered: someone who allows measurement once has not agreed
// to be followed around for the rest of the year.
export const ATTRIBUTION_DAYS = 30
export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
export const CLICK_KEYS = ["fbclid", "rdt_cid", "gclid", "gbraid", "wbraid"]

const DAY = 86400000

// A query parameter worth keeping: present, short enough to be an identifier
// and not a payload, and free of control characters. Anything else is
// dropped and not cleaned, since a value we had to repair is a value we
// cannot report faithfully.
export const value = (params, key) => {
  const text = params.get(key)
  return text && text.length <= 500 && !/[\u0000-\u001f\u007f]/.test(text) ? text : undefined
}

// What is known after this page load, given what was known before it.
//
// Click identifiers accumulate: a visitor can arrive from a Meta ad and later
// from a Google one, and both purchases are the same purchase to us. Campaigns
// replace, except `first_utm`, which is written once, the ad that found
// somebody and the ad they came back through are different questions.
//
// Returns null when nothing is left worth keeping, which is the signal to
// clear the record and not store an empty one.
export const captureAttribution = (previous, search, now = Date.now()) => {
  const params = new URLSearchParams(search)
  const current = previous?.expires_at > now ? structuredClone(previous) : { clicks: {}, utm: {} }
  let changed = false

  for (const key of CLICK_KEYS) {
    const id = value(params, key)
    if (id && current.clicks[key]?.id !== id) {
      current.clicks[key] = { id, captured_at: now }
      changed = true
    }
  }

  const utm = Object.fromEntries(UTM_KEYS.flatMap((key) => {
    const text = value(params, key)
    return text ? [[key, text]] : []
  }))
  if (Object.keys(utm).length) {
    current.utm = utm
    current.first_utm ??= utm
    changed = true
  }

  // Seeing the same campaign again extends the window; reading the page again
  // does not. Otherwise a bookmark would keep a campaign alive forever.
  if (changed) current.expires_at = now + ATTRIBUTION_DAYS * DAY

  for (const [key, click] of Object.entries(current.clicks)) {
    if (click.captured_at + ATTRIBUTION_DAYS * DAY <= now) delete current.clicks[key]
  }

  return current.expires_at > now ? current : null
}

// A Stripe Payment Link with the campaign on it, and the server's reference if
// there is one.
//
// Stripe accepts the five UTM values and rejects anything outside its own
// character set, so a campaign name it would refuse is left off and not
// breaking the link. The full value is on the server record either way.
export const paymentLink = (base, attribution, reference) => {
  const url = new URL(base)
  for (const key of UTM_KEYS) {
    const text = attribution?.utm?.[key]
    if (text && /^[a-zA-Z0-9_-]{1,150}$/.test(text)) url.searchParams.set(key, text)
  }
  if (/^attr_[a-f0-9]{32}$/.test(reference ?? "")) {
    url.searchParams.set("client_reference_id", reference)
  }
  return url.href
}
